import { Ban, CheckCircle2, RotateCcw, Warehouse } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { StorageFacilityModal } from '../components/StorageFacilityModal';
import { storageFacilitiesApi } from '../inventoryApi';
import type { StorageFacilityListItem } from '../inventoryTypes';

type TabKey = 'info';

/**
 * One storage facility.
 *
 * **One card of facts, not three.** The item's profile splits into three because its form has
 * three blocks a reader corrects independently; a facility has eight facts that read as one
 * section, and splitting them would put a cog over two rows.
 */
export function StorageFacilityProfilePage() {
  const { id } = useParams<{ id: string }>();
  const canReadOrgUnits = usePermission('org-units.read');

  const [facility, setFacility] = useState<StorageFacilityListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      storageFacilitiesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setFacility(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this facility.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await storageFacilitiesApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this facility.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Settings', to: '/inventory/settings' },
    { label: 'Storage Facilities', to: '/inventory/settings/storage-facilities' },
    facility?.name ?? 'Facility',
  ];

  if (error && !facility) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !facility) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { childCount } = facility;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="storage-facility-profile"
        header={
          <ProfileHeader
            id="storage-facility-profile-header"
            icon={<Warehouse className="h-6 w-6" aria-hidden="true" />}
            title={facility.name}
            meta={[
              facility.code,
              ...(facility.facilityType ? [facility.facilityType] : []),
              ...(facility.parentFacilityName ? [`Inside ${facility.parentFacilityName}`] : ['Top level']),
            ]}
            status={
              <StatusText tone={facility.isActive ? 'active' : 'inactive'}>
                {facility.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={facility.createdByName}
            createdAt={facility.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="storage-facility-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !facility.isActive ? (
            <ProfileBanner
              id="storage-facility-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This facility is deleted. It is no longer offered as a place to put stock, and its code is free for another facility to take."
              action={
                <PermissionGate permission="storage-facilities.delete">
                  <Button
                    id="storage-facility-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : childCount > 0 ? (
            <ProfileBanner
              id="storage-facility-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={`This facility holds ${childCount} other ${
                childCount === 1 ? 'facility' : 'facilities'
              }. It cannot be deleted until they are moved or deleted — leaving them under a hidden parent would strand them.`}
            />
          ) : (
            <ProfileBanner
              id="storage-facility-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This facility is offered as a place to put stock."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="storage-facility-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'storage-facilities.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Code', value: facility.code, mono: true },
              { label: 'Name', value: facility.name },
              { label: 'Type', value: facility.facilityType },
              { label: 'Sits inside', value: facility.parentFacilityName ?? 'Top level' },

              /*
                Drawn only for a reader who holds the org-unit read. Rendering an em dash
                instead would state that the store has no department, which is a different
                fact from this page not being allowed to say.
              */
              ...(canReadOrgUnits
                ? [{ label: 'Department', value: facility.orgUnitName ?? 'No department' }]
                : []),

              {
                label: 'Holds',
                value: (
                  <span className={`font-mono tabular-nums ${childCount === 0 ? 'text-slate-300' : ''}`}>
                    {childCount}
                  </span>
                ),
              },
              { label: 'Notes', value: facility.notes },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="storage-facility-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: facility.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(facility.createdAt)} className="font-mono">
                    {formatDateTime(facility.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: facility.updatedByName },
              {
                label: 'Updated',
                value: facility.updatedAt ? (
                  <time dateTime={toIso(facility.updatedAt)} className="font-mono">
                    {formatDateTime(facility.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <StorageFacilityModal
          open
          editing={facility}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
